import { useMemo } from 'react'
import Dinero from 'dinero.js'

export const usePriceHandler = () => {
  return useMemo(
    () => ({
      // Convert dollar amount to cents for backend
      toCents: (dollarAmount: string | number): number => {
        if (!dollarAmount || dollarAmount === '') return 0
        const amount = parseFloat(String(dollarAmount))
        if (isNaN(amount)) return 0
        return Math.round(amount * 100)
      },

      // Convert cents to dollars for display
      toDollars: (centsAmount: number): string => {
        if (!centsAmount && centsAmount !== 0) return ''
        const numAmount = Number(centsAmount)
        if (isNaN(numAmount)) return ''
        return Dinero({ amount: Math.round(numAmount) })
          .toUnit()
          .toString()
      },

      // Format cents amount for display with currency symbol
      formatCurrency: (centsAmount: number): string => {
        if (!centsAmount && centsAmount !== 0) return '$0.00'
        const numAmount = Number(centsAmount)
        if (isNaN(numAmount)) return '$0.00'
        return Dinero({ amount: Math.round(numAmount) }).toFormat('$0,0.00')
      },

      // Format for simple display (no currency symbol) - ONLY for cents (numbers)
      formatAmount: (centsAmount: number): string => {
        if (!centsAmount && centsAmount !== 0) return '0.00'
        const numAmount = Number(centsAmount)
        if (isNaN(numAmount)) return '0.00'
        return Dinero({ amount: Math.round(numAmount) }).toFormat('0,0.00')
      },

      // Format for display - handles both string (user input) and number (backend data)
      formatDisplay: (amount: number | string): string => {
        if (!amount && amount !== 0) return '0.00'

        if (typeof amount === 'string') {
          // User input - already in dollars, just format it
          const parsed = parseFloat(amount)
          if (isNaN(parsed)) return '0.00'
          return parsed.toFixed(2)
        } else {
          // Backend data - in cents, convert to dollars
          const numAmount = Number(amount)
          if (isNaN(numAmount)) return '0.00'
          return Dinero({ amount: Math.round(numAmount) }).toFormat('0,0.00')
        }
      },

      // Create Dinero instance for calculations
      createDinero: (centsAmount: number) => {
        const numAmount = Number(centsAmount) || 0
        return Dinero({ amount: Math.round(numAmount) })
      },

      // Parse user input (handles various formats)
      parseInput: (input: string): number => {
        if (!input || input.trim() === '') return 0
        const cleaned = input.replace(/[$,\s]/g, '')
        const amount = parseFloat(cleaned)
        if (isNaN(amount)) return 0
        return Math.round(amount * 100)
      },
    }),
    []
  )
}
